import { useTranslation } from "@sagot/i18n";
import { Icon, Skeleton, StatCard } from "@sagot/ui";
import { useFormatters } from "../shared/format";
import type { LeadStats } from "./lead-types";
import { useLeadStats } from "./use-leads";

const CHANNEL_ICON = { widget: "globe", messenger: "message-circle", mobile: "smartphone", webapp: "app-window" } as const;
type IconChannel = keyof typeof CHANNEL_ICON;
const isIconChannel = (key: string): key is IconChannel => key in CHANNEL_ICON;

function channelShares(stats: LeadStats): { key: string; pct: number }[] {
  const total = Object.values(stats.by_channel).reduce((n, v) => n + v, 0);
  if (total === 0) return [];
  return Object.entries(stats.by_channel)
    .map(([key, count]) => ({ key, pct: Math.round((count / total) * 100) }))
    .sort((a, b) => b.pct - a.pct);
}

function StatsSkeleton() {
  return (
    <section className="grid grid-cols-2 gap-4 lg:grid-cols-4" aria-hidden="true">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="card card-body space-y-2">
          <Skeleton className="h-4 w-[120px]" />
          <Skeleton className="h-8 w-[64px]" />
          <Skeleton className="h-3 w-[140px]" />
        </div>
      ))}
    </section>
  );
}

/** Four KPI cards from `GET /v1/leads/stats`; hidden quietly when the stats call fails. */
export function LeadStatsRow() {
  const { t } = useTranslation();
  const format = useFormatters();
  const stats = useLeadStats();
  if (stats.isPending) return <StatsSkeleton />;
  if (stats.isError) return null;
  const s = stats.data;
  const delta = s.leads_this_week - s.leads_previous_week;
  const shares = channelShares(s);
  const [top, ...rest] = shares;
  const channel = (key: string) => t(`engage.channels.${key}`, { defaultValue: key });
  return (
    <section className="grid grid-cols-2 gap-4 lg:grid-cols-4" aria-label={t("engage.leads.title")}>
      <StatCard
        label={t("engage.leads.stats.thisWeek")}
        icon="user-check"
        value={format.number(s.leads_this_week)}
        delta={delta === 0 ? undefined : { direction: delta > 0 ? "up" : "down", positive: delta > 0, label: t(delta > 0 ? "engage.leads.stats.deltaUp" : "engage.leads.stats.deltaDown", { count: delta }) }}
        footnote={delta === 0 ? t("engage.leads.stats.deltaFlat") : undefined}
      />
      <StatCard
        label={top ? t("engage.leads.stats.topChannel", { channel: channel(top.key) }) : t("engage.leads.stats.noChannel")}
        icon={top && isIconChannel(top.key) ? CHANNEL_ICON[top.key] : "globe"}
        value={top ? `${String(top.pct)}%` : "—"}
        footnote={rest.length ? rest.map((r) => `${channel(r.key)} ${String(r.pct)}%`).join(" · ") : t("engage.leads.stats.noLeadsYet")}
      />
      <StatCard label={t("engage.leads.stats.wonRate")} icon="target" value={format.percent(s.won_rate)} footnote={t("engage.leads.stats.wonHint")} />
      <div className="card card-body">
        <div className="flex items-center justify-between">
          <span className="stat-label">{t("engage.leads.stats.firstFollowUp")}</span>
          <Icon name="clock" className="text-fg-subtle" />
        </div>
        <div className="stat-value">{s.avg_first_follow_up_hours === null || s.avg_first_follow_up_hours < 0 ? "—" : t("engage.leads.stats.hours", { value: s.avg_first_follow_up_hours.toFixed(1) })}</div>
        <div className={s.idle_over_24h > 0 ? "mt-1 flex items-center gap-1 text-xs font-semibold text-warning" : "mt-1 text-xs text-fg-subtle"}>
          {s.idle_over_24h > 0 ? <Icon name="alert-triangle" className="size-3.5" /> : null}
          {s.idle_over_24h > 0 ? t("engage.leads.stats.waiting", { count: s.idle_over_24h }) : t("engage.leads.stats.noneWaiting")}
        </div>
      </div>
    </section>
  );
}
